(() => {
  "use strict";

  const SELECTOR = "[data-mr-marquee]";
  const instances = new WeakMap();

  class MountRigiMarquee {
    constructor(element) {
      this.element = element;
      this.viewport = element.querySelector("[data-mr-marquee-viewport]");
      this.track = element.querySelector("[data-mr-marquee-track]");
      this.source = element.querySelector("[data-mr-marquee-source]");

      this.animation = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.rebuildTimer = null;

      this.isHovered = false;
      this.hasFocus = false;
      this.isVisible = true;
      this.isDestroyed = false;
      this.isEditorPaused = false;

      this.boundHandleMouseEnter = this.handleMouseEnter.bind(this);
      this.boundHandleMouseLeave = this.handleMouseLeave.bind(this);
      this.boundHandleFocusIn = this.handleFocusIn.bind(this);
      this.boundHandleFocusOut = this.handleFocusOut.bind(this);
      this.boundScheduleRebuild = this.scheduleRebuild.bind(this);

      if (!this.viewport || !this.track || !this.source) {
        return;
      }

      this.init();
    }

    get settings() {
      return {
        direction: this.element.dataset.direction === "right" ? "right" : "left",
        speedDesktop: Math.max(
          1,
          Number.parseFloat(this.element.dataset.speedDesktop) || 90
        ),
        speedMobile: Math.max(
          1,
          Number.parseFloat(this.element.dataset.speedMobile) || 65
        ),
        mobileBreakpoint:
          Number.parseInt(this.element.dataset.mobileBreakpoint, 10) || 990,
        pauseOnHover: this.element.dataset.pauseOnHover === "true",
        pauseOffscreen: this.element.dataset.pauseOffscreen === "true",
        reducedMotion:
          this.element.dataset.reducedMotion === "slow" ? "slow" : "static"
      };
    }

    get isMobile() {
      return window.innerWidth < this.settings.mobileBreakpoint;
    }

    get prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    get currentSpeed() {
      const baseSpeed = this.isMobile
        ? this.settings.speedMobile
        : this.settings.speedDesktop;

      if (
        this.prefersReducedMotion &&
        this.settings.reducedMotion === "slow"
      ) {
        return Math.max(8, baseSpeed * 0.15);
      }

      return baseSpeed;
    }

    async init() {
      this.bindEvents();
      this.observeVisibility();
      this.observeSize();

      await this.waitForAssets();

      if (this.isDestroyed) {
        return;
      }

      this.build();
    }

    bindEvents() {
      if (!this.settings.pauseOnHover) {
        return;
      }

      this.element.addEventListener(
        "mouseenter",
        this.boundHandleMouseEnter
      );

      this.element.addEventListener(
        "mouseleave",
        this.boundHandleMouseLeave
      );

      this.element.addEventListener(
        "focusin",
        this.boundHandleFocusIn
      );

      this.element.addEventListener(
        "focusout",
        this.boundHandleFocusOut
      );
    }

    unbindEvents() {
      this.element.removeEventListener(
        "mouseenter",
        this.boundHandleMouseEnter
      );

      this.element.removeEventListener(
        "mouseleave",
        this.boundHandleMouseLeave
      );

      this.element.removeEventListener(
        "focusin",
        this.boundHandleFocusIn
      );

      this.element.removeEventListener(
        "focusout",
        this.boundHandleFocusOut
      );
    }

    observeSize() {
      if (!("ResizeObserver" in window)) {
        window.addEventListener("resize", this.boundScheduleRebuild);
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleRebuild();
      });

      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.source);
    }

    observeVisibility() {
      if (
        !this.settings.pauseOffscreen ||
        !("IntersectionObserver" in window)
      ) {
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          this.isVisible = Boolean(
            entry && entry.isIntersecting && entry.intersectionRatio > 0
          );

          this.updatePlayback();
        },
        {
          root: null,
          rootMargin: "200px 0px",
          threshold: 0
        }
      );

      this.intersectionObserver.observe(this.element);
    }

    async waitForAssets() {
      const imagePromises = Array.from(
        this.source.querySelectorAll("img")
      ).map((image) => {
        if (image.complete) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", resolve, { once: true });
        });
      });

      const fontPromise =
        document.fonts && document.fonts.ready
          ? document.fonts.ready.catch(() => undefined)
          : Promise.resolve();

      await Promise.all([...imagePromises, fontPromise]);
    }

    scheduleRebuild() {
      if (this.isDestroyed) {
        return;
      }

      window.clearTimeout(this.rebuildTimer);

      this.rebuildTimer = window.setTimeout(() => {
        this.build();
      }, 120);
    }

    removeClones() {
      this.track
        .querySelectorAll("[data-mr-marquee-clone]")
        .forEach((clone) => clone.remove());
    }

    createClone() {
      const clone = this.source.cloneNode(true);

      clone.removeAttribute("data-mr-marquee-source");
      clone.setAttribute("data-mr-marquee-clone", "");
      clone.setAttribute("aria-hidden", "true");

      clone
        .querySelectorAll("[id]")
        .forEach((item) => item.removeAttribute("id"));

      clone
        .querySelectorAll("[data-shopify-editor-block]")
        .forEach((item) => {
          item.removeAttribute("data-shopify-editor-block");
        });

      clone
        .querySelectorAll(
          "a, button, input, select, textarea, [tabindex]"
        )
        .forEach((focusable) => {
          focusable.setAttribute("tabindex", "-1");
          focusable.setAttribute("aria-hidden", "true");
        });

      this.track.appendChild(clone);

      return clone;
    }

    build() {
      if (
        this.isDestroyed ||
        !window.gsap ||
        !this.viewport.offsetWidth ||
        !this.source.children.length
      ) {
        return;
      }

      const previousProgress = this.animation
        ? this.animation.progress()
        : 0;

      if (this.animation) {
        this.animation.kill();
        this.animation = null;
      }

      this.removeClones();

      window.gsap.set(this.track, {
        clearProps: "transform"
      });

      const sourceWidth = this.source.getBoundingClientRect().width;
      const viewportWidth = this.viewport.getBoundingClientRect().width;

      if (!sourceWidth || !viewportWidth) {
        return;
      }

      const requiredWidth = viewportWidth + sourceWidth * 2;
      let trackWidth = sourceWidth;
      let cloneCount = 0;
      const maximumClones = 100;

      while (
        trackWidth < requiredWidth &&
        cloneCount < maximumClones
      ) {
        this.createClone();

        cloneCount += 1;
        trackWidth += sourceWidth;
      }

      while (cloneCount < 2) {
        this.createClone();

        cloneCount += 1;
      }

      this.element.classList.remove("is-static");
      this.element.classList.add("is-ready");

      if (
        this.prefersReducedMotion &&
        this.settings.reducedMotion === "static"
      ) {
        this.element.classList.add("is-static");

        window.gsap.set(this.track, {
          x: 0
        });

        return;
      }


      const duration = sourceWidth / this.currentSpeed;
      const movingLeft = this.settings.direction === "left";

      /*
        Beide Laufrichtungen verwenden denselben nahtlosen Bereich:

        links:  0 bis -sourceWidth
        rechts: -sourceWidth bis 0

        Der GSAP-Modifier hält die Position dauerhaft innerhalb
        dieses Bereichs. Dadurch gibt es weder einen Sprung noch
        eine leere Stelle zwischen den Wiederholungen.
      */

      const wrapX = window.gsap.utils.wrap(
        -sourceWidth,
        0
      );

      const startX = movingLeft
        ? -sourceWidth * previousProgress
        : -sourceWidth + sourceWidth * previousProgress;

      window.gsap.set(this.track, {
        x: startX
      });

      this.animation = window.gsap.to(this.track, {
        x: movingLeft
          ? `-=${sourceWidth}`
          : `+=${sourceWidth}`,
        duration,
        ease: "none",
        repeat: -1,
        modifiers: {
          x: (value) => {
            const numericValue = Number.parseFloat(value);

            return `${wrapX(numericValue)}px`;
          }
        }
      });

      this.updatePlayback();

    }

    handleMouseEnter() {
      this.isHovered = true;
      this.updatePlayback();
    }

    handleMouseLeave() {
      this.isHovered = false;
      this.updatePlayback();
    }

    handleFocusIn() {
      this.hasFocus = true;
      this.updatePlayback();
    }

    handleFocusOut(event) {
      const nextFocusedElement = event.relatedTarget;

      this.hasFocus = Boolean(
        nextFocusedElement &&
        this.element.contains(nextFocusedElement)
      );

      this.updatePlayback();
    }

    updatePlayback() {
      if (!this.animation) {
        return;
      }

      const shouldPause =
        this.isEditorPaused ||
        (this.settings.pauseOnHover &&
          (this.isHovered || this.hasFocus)) ||
        (this.settings.pauseOffscreen && !this.isVisible) ||
        document.hidden;

      if (shouldPause) {
        this.animation.pause();
      } else {
        this.animation.resume();
      }
    }

    pauseForEditor() {
      this.isEditorPaused = true;
      this.updatePlayback();
    }

    resumeAfterEditor() {
      this.isEditorPaused = false;
      this.updatePlayback();
    }

    destroy() {
      this.isDestroyed = true;

      window.clearTimeout(this.rebuildTimer);

      this.unbindEvents();

      if (this.animation) {
        this.animation.kill();
        this.animation = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener(
          "resize",
          this.boundScheduleRebuild
        );
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      this.removeClones();

      this.element.classList.remove("is-ready", "is-static");

      if (window.gsap) {
        window.gsap.set(this.track, {
          clearProps: "transform"
        });
      }

      instances.delete(this.element);
    }
  }

  function initializeElement(element) {
    if (!element || instances.has(element)) {
      return;
    }

    const instance = new MountRigiMarquee(element);

    instances.set(element, instance);
  }

  function initializeMarquees(scope = document) {
    if (scope.matches && scope.matches(SELECTOR)) {
      initializeElement(scope);
    }

    scope
      .querySelectorAll?.(SELECTOR)
      .forEach((element) => initializeElement(element));
  }

  function destroyMarquees(scope) {
    if (scope.matches && scope.matches(SELECTOR)) {
      instances.get(scope)?.destroy();
    }

    scope
      .querySelectorAll?.(SELECTOR)
      .forEach((element) => instances.get(element)?.destroy());
  }

  function findInstanceFromEvent(event) {
    const marquee = event.target.closest?.(SELECTOR);

    return marquee ? instances.get(marquee) : null;
  }

  function start() {
    if (!window.gsap) {
      window.setTimeout(start, 50);
      return;
    }

    if (window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
    }

    initializeMarquees(document);

    document.addEventListener("visibilitychange", () => {
      document.querySelectorAll(SELECTOR).forEach((element) => {
        instances.get(element)?.updatePlayback();
      });
    });

    document.addEventListener("shopify:section:load", (event) => {
      initializeMarquees(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      destroyMarquees(event.target);
    });

    document.addEventListener("shopify:section:reorder", () => {
      document.querySelectorAll(SELECTOR).forEach((element) => {
        instances.get(element)?.scheduleRebuild();
      });
    });

    document.addEventListener("shopify:block:select", (event) => {
      findInstanceFromEvent(event)?.pauseForEditor();
    });

    document.addEventListener("shopify:block:deselect", (event) => {
      findInstanceFromEvent(event)?.resumeAfterEditor();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, {
      once: true
    });
  } else {
    start();
  }
})();
